import React from 'react';
import { gql, useQuery } from '@common/hooks';
import moment from 'moment';
import { StaffingRequestSubmissionForm } from '@jobs/pages';
import { StaffingRequestWithdrawModal } from './staffing-request-withdraw-modal';
import {
  Conversation,
  Drawer,
  ErrorHandler,
  LoadingState,
  Tabs,
} from '@common/components';
import {
  CommentInfoFragment,
  CommentInfoFragmentType,
  EmploymentTypes,
  SimpleFileFragment,
  SimpleFileFragmentType,
} from '@common/graphql';
import { WorksProfileType } from '@clinician-profile/graphql';
import { StaffingRequestType } from '@applicant-flow/types';

const DRAWER_QUERY = gql`
  query StaffingRequestReviewSubmissionDrawerQuery($applicationId: ID!) {
    staffingRequestApplication(id: $applicationId) {
      id
      availableToStartAt
      timeoffDescription
      interviewAvailability
      employmentType
      canWithdraw {
        value
      }
      doNotRehire
      doNotRehireComment
      staffingRequest {
        id
        staffingRequestType
      }
      worksProfile {
        id
        fullName
        worksSubmittalPackets {
          id
          files {
            id
            createdAt
            downloadUrl
            filename
            previewUrl
          }
        }
      }
      state
      canWithdraw {
        value
      }
      comments {
        id
        ...CommentInfoFragment
      }
      worksSubmittalPacket {
        id
        files {
          ...SimpleFileFragment
        }
      }
    }
  }
  ${CommentInfoFragment}
  ${SimpleFileFragment}
`;

type SubmittalPacket = {
  id: string;
  files: SimpleFileFragmentType[];
};

type DrawerQueryResponse = {
  staffingRequestApplication: {
    id: string;
    availableToStartAt: string;
    timeoffDescription: string;
    interviewAvailability: string;
    canWithdraw: {
      value: boolean;
    };
    doNotRehire: boolean;
    doNotRehireComment: string;
    employmentType: EmploymentTypes;
    worksSubmittalPacket: SubmittalPacket;
    comments: CommentInfoFragmentType[];
    staffingRequest: StaffingRequestType;
    worksProfile: WorksProfileType;
  };
};

type DrawerQueryArgs = {
  applicationId: string;
};

export function StaffingRequestReviewSubmissionDrawer({
  open,
  onClose,
  applicationId,
  currentTab,
}: {
  open: boolean;
  onClose: () => void;
  applicationId?: string;
  currentTab?: string;
}) {
  const { data, loading, error } = useQuery<
    DrawerQueryResponse,
    DrawerQueryArgs
  >(DRAWER_QUERY, {
    variables: { applicationId: applicationId || '' },
    skip: !applicationId,
  });

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="StaffingRequestReviewSubmissionDrawer"
        messageType="contextual"
      />
    );
  }

  const application = data?.staffingRequestApplication;
  const commentsCount = application?.comments.length || 0;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={
          loading
            ? 'Loading...'
            : `${application?.worksProfile.fullName}'s submission`
        }
        footer={
          <>
            {application?.canWithdraw.value && (
              <StaffingRequestWithdrawModal
                staffingRequestId={application.staffingRequest.id}
                applicationId={application.id}
              />
            )}
          </>
        }
      >
        <>
          {loading || !application ? (
            <LoadingState />
          ) : (
            <Tabs
              defaultActiveKey={
                commentsCount > 0 || currentTab === 'notes'
                  ? 'notes'
                  : 'summary'
              }
            >
              <Tabs.Pane tab="Submission" key="summary">
                <section className="staffing-request-review-submission-drawer__pane-content">
                  <StaffingRequestSubmissionForm
                    formSubmitting={true}
                    initialValues={{
                      availableToStartAt:
                        application.availableToStartAt &&
                        moment(application.availableToStartAt),
                      timeoffDescription: application.timeoffDescription,
                      interviewAvailability: application.interviewAvailability,
                      submittalPacket: application.worksSubmittalPacket,
                      reasonForSubmission: application.doNotRehireComment,
                      employmentType: application.employmentType,
                    }}
                    doNotRehire={application.doNotRehire}
                    worksProfile={application.worksProfile}
                    staffingRequestType={
                      application.staffingRequest.staffingRequestType
                    }
                  />
                </section>
              </Tabs.Pane>
              <Tabs.Pane
                tab={!commentsCount ? 'Notes' : `Notes (${commentsCount})`}
                key="notes"
              >
                <section className="staffing-request-review-submission-drawer__pane-content">
                  <Conversation
                    emptyMessage="No notes to show from this candidate's application"
                    posts={application.comments.map(
                      (comment: CommentInfoFragmentType) => ({
                        id: comment.id,
                        message: comment.contentAsText,
                        time: comment.createdAt,
                      })
                    )}
                  />
                </section>
              </Tabs.Pane>
            </Tabs>
          )}
        </>
      </Drawer>
      <style jsx>{`
        .staffing-request-review-submission-drawer__pane-content {
          margin-top: 24px;
        }
      `}</style>
    </>
  );
}

StaffingRequestReviewSubmissionDrawer.fragments = {
  StaffingRequestReviewSubmissionInfo: gql`
    fragment StaffingRequestReviewSubmissionInfo on StaffingRequestApplication {
      id
      availableToStartAt
      timeoffDescription
      interviewAvailability
      worksSubmittalPacket {
        id
        files {
          id
          filename
          downloadUrl
          createdAt
        }
      }
      comments {
        ...CommentInfoFragment
      }
    }
    ${CommentInfoFragment}
  `,
};
